import { SvgIconComponent } from '@mui/icons-material';
import dayjs from 'dayjs';
import { z } from 'zod';

import { GroupType } from '../services/scan-types/schemas';
import { BoxRes } from './Box';
import { Logger, LoggerId } from './Logger';

export const ScanItemTypeEnumSchema = z.enum(['SCAN', 'BOX']);
export type ScanItemType = z.infer<typeof ScanItemTypeEnumSchema>;
export const ScanItemTypeEnum = ScanItemTypeEnumSchema.enum;

export enum ScanActionType {
    IMPORT = 'IMPORT',
    STATISTICS = 'STATISTICS',
    CLEAR = 'CLEAR',
    COMPLETE = 'COMPLETE',
    EDIT = 'EDIT',
    ADD = 'ADD'
}

export type ScanActionItem = { type: ScanActionType; text: string; icon: SvgIconComponent };

export type OverviewMode = 'ARRIVING_LOGGERS' | 'SCANNING';
export type ScanOrBoxStatus = 'COMPLETE' | 'IN_PROGRESS';

// Response model from GET /scans/scanId/import and /scans/box/{boxId}/import
export const ImportScanItemResSchema = z.object({
    message: z.string(),
    duplicate_ids_removed: z.array(z.string()),
    logger_ids_to_import: z.array(z.string())
});
export type ImportScanItemRes = z.infer<typeof ImportScanItemResSchema>;

// Err response model from GET /scans/scanId/import and /scans/box/{boxId}/import
export const ImportMismatchResSchema = z.object({
    loggerId: z.string(),
    mostRecentScanId: z.number(),
    mostRecentBoxId: z.number().optional(),
    scannedByUsername: z.string(),
    scannedTime: z
        .string()
        .refine((value) => dayjs(value).isValid())
        .transform(dayjs)
});
export type ImportMismatch = z.infer<typeof ImportMismatchResSchema>;

export const ImportPayloadSchema = z.object({
    sourceType: ScanItemTypeEnumSchema,
    sourceId: z.string(),
    loggerIdToVerify: z.string()
});
export type ImportScanPayload = z.infer<typeof ImportPayloadSchema>;

export interface GetScansQueryParams {
    page: number;
    show: number;
    filterCriteria?: string;
    arriving?: boolean;
    userLocations?: Array<string>;
}

export interface ScanBoxCommon {
    id: number;
    scanTypeName: string;
    scanTypeGroupType: GroupType;
    createdByUsername: string;
    createdTime: string;
    statusName: ScanOrBoxStatus;
    loggerCount: number;
    locationName: string;
    loggers: Array<Logger>;
    deletedByUsername?: string;
    deletedTime?: string;
}

// Response from GET /scans/scanId
export interface ScanRes extends ScanBoxCommon {
    assignedCustomerId: number | null;
    allowFailedChecks: boolean;
    boxCount: number;
    boxes: Array<BoxRes>;
    trackingId: string;
    salesOrderId: string;
    received: boolean;
    receivedByUsername?: string;
    receivedTime?: string;
    destinationName: string;
    isBoxScan: boolean;
}

export interface LoggerShipmentRelease {
    loggerId: LoggerId;
    shipmentId: number;
    message: string;
}

export interface ScanOrBoxSubmitRes {
    loggers: Record<LoggerId, Logger>;
    scanId: number;
    scanType: string;
    scanStatus: ScanOrBoxStatus;
    success: boolean;
    boxId?: number;
    boxStatus?: ScanOrBoxStatus;
    actions?: {
        releasedShipments: ReleasedShipments;
    };
}

export interface ReleasedShipments {
    successfullyReleased: Array<LoggerShipmentRelease>;
    failedToRelease: Array<LoggerShipmentRelease>;
}

// Payload for PATCH /scans/{scanId}
export interface PatchScanPayload {
    salesOrderId?: string;
    scanTypeId?: number;
    trackingId?: string;
    assignedCustomerId?: number;
    destinationName?: string;
    received?: boolean;
}
